/**
 * Vẽ ma trận QR lên canvas, chừa một ô trống ở giữa để đặt logo.
 *
 * KHÔNG dùng `QRCode.toCanvas` như chỗ khác trong app: hàm đó tự vẽ trọn ảnh
 * và không cho biết ma trận có bao nhiêu ô, nên không thể canh logo rơi đúng
 * biên ô. Ở đây lấy thẳng ma trận bằng `QRCode.create` rồi tự vẽ từng ô —
 * nhờ vậy ô trống giữa mã luôn trùng khít lưới, máy quét không gặp ô nửa vời.
 */

import QRCode from "qrcode";

/**
 * Mức sửa lỗi H — khôi phục được 30% dữ liệu hỏng. BẮT BUỘC ở đây: đặt logo
 * lên giữa mã là cố ý xoá một mảng ô, mức thấp hơn sẽ làm mã không quét được.
 */
const ERROR_CORRECTION = "H" as const;

/**
 * Bề ngang ô logo, tính theo tỉ lệ cạnh mã QR.
 *
 * 0.19 lấy từ chính 3 ảnh mockup CEO duyệt (logo rộng ~100px trên mã ~530px).
 * Diện tích bị che ≈ 3.6% — còn rất xa ngưỡng 30% mà mức H chịu được, nên mã
 * vẫn quét tốt cả khi ảnh bị nén lại lúc gửi qua Zalo.
 *
 * ĐỪNG nới con số này lên cho logo to đẹp hơn: che quá nhiều là mã chết, mà
 * lỗi đó chỉ lộ ra khi khách đứng trước quầy không quét được.
 */
const LOGO_RATIO = 0.19;

/** Viền trắng quanh logo, tính theo tỉ lệ cạnh mã — tách logo khỏi các ô đen. */
const LOGO_PADDING_RATIO = 0.012;

/** Chỗ vẽ dấu hiệu bên trong ô logo — xem `fitLogoInBox`. */
export type LogoFit = { dx: number; dy: number; width: number; height: number };

/**
 * Đặt dấu hiệu thương hiệu vừa ô vuông giữa mã QR, GIỮ NGUYÊN TỈ LỆ ảnh.
 *
 * Bốn thương hiệu không cùng tỉ lệ: Ảnh Thẻ/Profile vuông 280×280, Travel vuông
 * 160×160, còn Chụp Ảnh nằm ngang 127×75. Vẽ thẳng vào ô vuông (cách cũ) thì
 * ảnh ngang bị kéo cao 1,7 lần — logo méo mà không lỗi nào nổi lên, chỉ lộ ra
 * khi ai đó nhìn kỹ tấm ảnh đã gửi cho khách.
 *
 * Ảnh vuông cho ra đúng kết quả như trước (lấp đầy ô, lề 0) nên phiếu của hai
 * thương hiệu cũ không đổi một pixel nào.
 */
export function fitLogoInBox(imageWidth: number, imageHeight: number, box: number): LogoFit {
  // Ảnh chưa tải xong báo kích thước 0 — lấp đầy ô thay vì chia cho 0 rồi ra NaN.
  if (imageWidth <= 0 || imageHeight <= 0) return { dx: 0, dy: 0, width: box, height: box };
  const scale = Math.min(box / imageWidth, box / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return { dx: (box - width) / 2, dy: (box - height) / 2, width, height };
}

export type QRMatrix = {
  /** Số ô mỗi cạnh (kể cả viền định vị), chưa tính lề trắng. */
  size: number;
  /** `true` = ô đen. */
  isDark: (row: number, col: number) => boolean;
};

/**
 * Dựng ma trận QR từ chuỗi VietQR.
 *
 * @throws khi chuỗi rỗng hoặc dài quá sức chứa của QR — để người gọi hiện lỗi
 *   thay vì vẽ ra một ảnh trống không ai biết là hỏng.
 */
export function buildQRMatrix(payload: string): QRMatrix {
  const qr = QRCode.create(payload, { errorCorrectionLevel: ERROR_CORRECTION });
  const { size, data } = qr.modules;
  return {
    size,
    isDark: (row, col) => data[row * size + col] === 1,
  };
}

export type DrawQROptions = {
  ctx: CanvasRenderingContext2D;
  matrix: QRMatrix;
  /** Góc trên trái vùng vẽ mã (đã trừ lề trắng bao ngoài). */
  x: number;
  y: number;
  /** Cạnh vùng vẽ mã, tính bằng pixel. */
  size: number;
  /** Ảnh logo đặt giữa mã. Vắng mặt = vẽ mã trơn, không chừa ô trống. */
  logo?: HTMLImageElement | undefined;
  /** Màu ô đen. */
  foreground: string;
  /** Màu nền dưới mã — cũng là màu viền quanh logo. */
  background: string;
};

/**
 * Vẽ mã QR kèm logo giữa.
 *
 * Ô được vẽ theo biên số nguyên pixel (`Math.round` hai đầu thay vì làm tròn
 * bề rộng): làm tròn bề rộng thì sai số dồn lại, tới cuối hàng lưới lệch hẳn
 * một ô và mã mờ nhoè. Cách này giữ mọi ô sát nhau, không hở kẽ trắng.
 */
export function drawQR({
  ctx,
  matrix,
  x,
  y,
  size,
  logo,
  foreground,
  background,
}: DrawQROptions): void {
  const cell = size / matrix.size;

  ctx.fillStyle = background;
  ctx.fillRect(x, y, size, size);

  ctx.fillStyle = foreground;
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (!matrix.isDark(row, col)) continue;
      const left = x + Math.round(col * cell);
      const top = y + Math.round(row * cell);
      const right = x + Math.round((col + 1) * cell);
      const bottom = y + Math.round((row + 1) * cell);
      ctx.fillRect(left, top, right - left, bottom - top);
    }
  }

  if (!logo) return;

  const logoSize = size * LOGO_RATIO;
  const padding = size * LOGO_PADDING_RATIO;
  const boxSize = logoSize + padding * 2;
  const boxX = x + (size - boxSize) / 2;
  const boxY = y + (size - boxSize) / 2;

  // Nền trắng bo góc + viền vàng — giống hệt 3 mockup, đồng thời tách logo
  // khỏi các ô đen xung quanh để máy quét không đọc nhầm logo thành dữ liệu.
  roundedRectPath(ctx, boxX, boxY, boxSize, boxSize, boxSize * 0.22);
  ctx.fillStyle = background;
  ctx.fill();
  ctx.lineWidth = Math.max(2, size * 0.004);
  ctx.strokeStyle = "#C9A66A";
  ctx.stroke();

  const fit = fitLogoInBox(logo.naturalWidth || logo.width, logo.naturalHeight || logo.height, logoSize);
  ctx.drawImage(logo, boxX + padding + fit.dx, boxY + padding + fit.dy, fit.width, fit.height);
}

/** Vẽ đường bo góc chữ nhật vào path hiện tại (không tô, không kẻ). */
export function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
